import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Receipt, 
  Users, 
  Search, 
  Calendar,
  DollarSign,
  Eye,
  Check,
  X,
  ShieldCheck,
  UserCheck,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const NASPOSAdmin = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // Sales state
  const [sales, setSales] = useState([]);
  const [salesSummary, setSalesSummary] = useState({ totalSales: 0, totalTransactions: 0 });
  const [loadingSales, setLoadingSales] = useState(false);
  const [searchDate, setSearchDate] = useState('');
  const [searchCashier, setSearchCashier] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  
  // Users state
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      fetchSales();
      fetchAuthorizedUsers();
    }
  }, [isAdmin]);

  const fetchSales = async () => {
    setLoadingSales(true);
    try {
      const params = new URLSearchParams();
      if (searchDate) {
        params.append('date_from', searchDate);
        params.append('date_to', searchDate);
      }
      if (searchCashier) {
        params.append('cashier', searchCashier);
      }
      
      const response = await axios.get(`${API}/api/nas/pos/admin/sales?${params.toString()}`);
      setSales(response.data.sales || []);
      setSalesSummary(response.data.summary || { totalSales: 0, totalTransactions: 0 });
    } catch (error) {
      console.error('Error fetching sales:', error);
      toast.error('Failed to load sales');
    } finally {
      setLoadingSales(false);
    }
  };

  const fetchAuthorizedUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/api/nas/pos/admin/authorized-users`);
      setAuthorizedUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleToggleAccess = async (userId, currentAccess) => {
    setUpdatingUser(userId);
    try {
      await axios.put(`${API}/api/nas/pos/admin/authorize-user/${userId}?authorize=${!currentAccess}`);
      toast.success(`User ${!currentAccess ? 'authorized' : 'access revoked'}`);
      fetchAuthorizedUsers();
    } catch (error) {
      toast.error('Failed to update user access');
    } finally {
      setUpdatingUser(null);
    }
  };

  const formatCurrency = (amount) => `₱${(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  
  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ShieldCheck size={48} className="mx-auto text-slate-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Admin Access Required</h2>
          <p className="text-slate-500">Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">POS Administration</h1>
          <p className="text-slate-500">View all sales and manage user access</p>
        </div>
      </div>

      <Tabs defaultValue="sales" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sales" data-testid="sales-tab">
            <Receipt size={16} className="mr-2" />
            All Sales
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab">
            <Users size={16} className="mr-2" />
            User Access
          </TabsTrigger>
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <Label>Cashier</Label>
                  <Input
                    placeholder="Search cashier..."
                    value={searchCashier}
                    onChange={(e) => setSearchCashier(e.target.value)}
                    className="w-48"
                  />
                </div>
                <Button onClick={fetchSales} disabled={loadingSales}>
                  {loadingSales ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search size={16} className="mr-2" />}
                  Search
                </Button>
                <Button variant="outline" onClick={() => { setSearchDate(''); setSearchCashier(''); fetchSales(); }}>
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Sales</p>
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(salesSummary.totalSales)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Receipt className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Transactions</p>
                    <p className="text-xl font-bold text-blue-600">{salesSummary.totalTransactions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sales Table */}
          <Card>
            <CardHeader>
              <CardTitle>Sales Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Receipt #</th>
                      <th className="text-left py-3 px-2">Date/Time</th>
                      <th className="text-left py-3 px-2">Cashier</th>
                      <th className="text-left py-3 px-2">Customer</th>
                      <th className="text-left py-3 px-2">Items</th>
                      <th className="text-right py-3 px-2">Amount</th>
                      <th className="text-center py-3 px-2">Payment</th>
                      <th className="text-center py-3 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingSales ? (
                      <tr>
                        <td colSpan="8" className="py-8 text-center text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading sales...
                        </td>
                      </tr>
                    ) : sales.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="py-8 text-center text-slate-500">
                          No sales found
                        </td>
                      </tr>
                    ) : (
                      sales.map((sale) => (
                        <tr key={sale.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-2 font-mono text-xs">{sale.receiptNumber}</td>
                          <td className="py-3 px-2">{formatDateTime(sale.createdAt)}</td>
                          <td className="py-3 px-2">{sale.cashierName}</td>
                          <td className="py-3 px-2">{sale.customerName || 'Walk-in'}</td>
                          <td className="py-3 px-2">{sale.items?.length || 0} items</td>
                          <td className="py-3 px-2 text-right font-medium">{formatCurrency(sale.totalAmount)}</td>
                          <td className="py-3 px-2 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              sale.paymentMethod === 'cash' ? 'bg-emerald-100 text-emerald-700' :
                              sale.paymentMethod === 'gcash' ? 'bg-blue-100 text-blue-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {sale.paymentMethod?.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedSale(sale)}
                            >
                              <Eye size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck size={20} />
                POS User Authorization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                Manage which users can access the POS system. Default users (Geraldine, Cristel, Kristine, Nicole) always have access.
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">User</th>
                      <th className="text-left py-3 px-2">Username</th>
                      <th className="text-left py-3 px-2">Role</th>
                      <th className="text-center py-3 px-2">Access Type</th>
                      <th className="text-center py-3 px-2">POS Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingUsers ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading users...
                        </td>
                      </tr>
                    ) : (
                      authorizedUsers.map((u) => (
                        <tr key={u.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-2 font-medium">{u.full_name || u.username}</td>
                          <td className="py-3 px-2 text-slate-500">{u.username}</td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              u.access_type === 'admin' ? 'bg-purple-100 text-purple-700' :
                              u.access_type === 'default' ? 'bg-blue-100 text-blue-700' :
                              u.access_type === 'explicit' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {u.access_type === 'admin' ? 'Admin' :
                               u.access_type === 'default' ? 'Default User' :
                               u.access_type === 'explicit' ? 'Authorized' : 'No Access'}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            {u.role === 'admin' || u.access_type === 'default' ? (
                              <div className="flex items-center justify-center gap-2">
                                <Check size={16} className="text-emerald-600" />
                                <span className="text-xs text-slate-500">Always</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                {updatingUser === u.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Switch
                                    checked={u.pos_access}
                                    onCheckedChange={() => handleToggleAccess(u.id, u.pos_access)}
                                  />
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sale Detail Dialog */}
      <Dialog open={!!selectedSale} onOpenChange={() => setSelectedSale(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sale Details</DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Receipt #</p>
                  <p className="font-mono">{selectedSale.receiptNumber}</p>
                </div>
                <div>
                  <p className="text-slate-500">Date/Time</p>
                  <p>{formatDateTime(selectedSale.createdAt)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Cashier</p>
                  <p>{selectedSale.cashierName}</p>
                </div>
                <div>
                  <p className="text-slate-500">Customer</p>
                  <p>{selectedSale.customerName || 'Walk-in'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="font-medium mb-2">Items:</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedSale.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm bg-slate-50 p-2 rounded">
                      <span>{item.productName} x{item.quantity} {item.unit}</span>
                      <span className="font-medium">{formatCurrency(item.totalPrice)}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedSale.subtotal)}</span>
                </div>
                {selectedSale.discount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(selectedSale.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-emerald-600">{formatCurrency(selectedSale.totalAmount)}</span>
                </div>
                {selectedSale.paymentMethod === 'cash' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span>Cash Received</span>
                      <span>{formatCurrency(selectedSale.amountReceived)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Change</span>
                      <span>{formatCurrency(selectedSale.change)}</span>
                    </div>
                  </>
                )}
              </div>
              
              <div className="border-t pt-4 text-center">
                <span className={`px-3 py-1 rounded-full text-sm ${
                  selectedSale.paymentMethod === 'cash' ? 'bg-emerald-100 text-emerald-700' :
                  selectedSale.paymentMethod === 'gcash' ? 'bg-blue-100 text-blue-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  Paid via {selectedSale.paymentMethod?.toUpperCase()}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASPOSAdmin;
